import React, { useRef, useEffect } from 'react'
import styled from 'styled-components'
import { color } from 'styles/colors'

type Props = React.PropsWithChildren<{
  className?: string;
  isOpen: boolean;
  onClose: (event?: React.MouseEvent | React.KeyboardEvent, isClickOutsideDropdown?: boolean) => void;
  ariaLabelsToIgnoreOnOutsideClick?: string[];
}>

const Dropdown = ({ className, isOpen, onClose, ariaLabelsToIgnoreOnOutsideClick = [], children }: Props) => {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const isClickOutsideDropdown = !ref.current?.contains(event.target)
      const isIgnoredElementClicked = ariaLabelsToIgnoreOnOutsideClick.includes(event.target.ariaLabel)
      const isClickInsideDialog = event.target.closest('[role="dialog"]')?.contains(event.target)

      if (isClickOutsideDropdown && !isIgnoredElementClicked && !isClickInsideDialog) {
        onClose(event, isClickOutsideDropdown)
      }
    }

    document.addEventListener('mouseup', handleOutsideClick)

    return () => {
      document.removeEventListener('mouseup', handleOutsideClick)
    }
  }, [ref, ariaLabelsToIgnoreOnOutsideClick, onClose])

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose(event)
      }
    }

    document.addEventListener('keydown', handleEscapeKey)

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [onClose])

  return (
    <S.Container {...{ isOpen, className, ref }}>
      {children}
    </S.Container>
  )
}

export default Dropdown

const S = {
  Container: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isOpen'].includes(prop)
  })<{ isOpen: boolean }>`
    display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
    position: absolute;
    z-index: 1000;
    box-shadow: 0px 3px 7px 0px ${color('shadowLight')};
  `
}
